import { Box, Typography, LinearProgress, Collapse } from "@mui/material";
import { useContext, useState, useMemo } from "react";
import { ChatContext } from "../../contexts/chat/context";
import { SQLQuery, DocSearch } from "../../contexts/chat/type";

// Union type for combined events
type AgentEvent =
  | { type: 'sql'; iteration: number; data: SQLQuery }
  | { type: 'doc'; iteration: number; data: DocSearch };

export const AgentThinking = () => {
  const { agentState } = useContext(ChatContext);
  const [showDetails, setShowDetails] = useState(true);

  // Debug logging
  console.log("AgentThinking render - status:", agentState.status);

  // Combine and sort all events by iteration number
  const sortedEvents = useMemo((): AgentEvent[] => {
    const events: AgentEvent[] = [
      ...agentState.sqlQueries.map((q): AgentEvent => ({ type: 'sql', iteration: q.iteration, data: q })),
      ...agentState.docSearches.map((d): AgentEvent => ({ type: 'doc', iteration: d.iteration, data: d })),
    ];
    return events.sort((a, b) => a.iteration - b.iteration);
  }, [agentState.sqlQueries, agentState.docSearches]);

  // Only show when agent is streaming
  if (agentState.status !== "streaming") {
    console.log("AgentThinking - NOT rendering (status is not streaming)");
    return null;
  }

  console.log("AgentThinking - RENDERING (status is streaming)");

  const progress =
    agentState.maxIterations > 0
      ? (agentState.currentIteration / agentState.maxIterations) * 100
      : 0;

  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        m: 1,
        p: 2,
        borderRadius: "8px",
        borderBottomLeftRadius: 0,
        backgroundColor: "white",
        border: "1px solid",
        borderColor: "primary.light",
        maxWidth: "90%",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            animation: "pulse 1.5s infinite",
            "@keyframes pulse": {
              "0%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.5, transform: "scale(1.2)" },
              "100%": { opacity: 1, transform: "scale(1)" },
            },
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
          Thinking...
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mb: 1.5,
          height: 4,
          borderRadius: 2,
          backgroundColor: "grey.200",
          "& .MuiLinearProgress-bar": {
            borderRadius: 2,
          },
        }}
      />

      {/* Current reasoning */}
      {agentState.reasoning && (
        <Box
          sx={{
            backgroundColor: "grey.50",
            p: 2,
            borderRadius: 1,
            mb: 1.5,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              fontStyle: "italic",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {agentState.reasoning}
          </Typography>
        </Box>
      )}

      {/* Activity indicators */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {agentState.docSearches.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "secondary.light",
              color: "secondary.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <span>📚</span>
            <span>{agentState.docSearches.length} doc search{agentState.docSearches.length > 1 ? "es" : ""}</span>
          </Box>
        )}
        {agentState.sqlQueries.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "success.light",
              color: "success.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <span>🔍</span>
            <span>{agentState.sqlQueries.length} SQL quer{agentState.sqlQueries.length > 1 ? "ies" : "y"}</span>
          </Box>
        )}
        {agentState.charts.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "warning.light",
              color: "warning.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            <span>📊</span>
            <span>{agentState.charts.length} chart{agentState.charts.length > 1 ? "s" : ""}</span>
          </Box>
        )}
      </Box>

      {/* Expandable details */}
      {(agentState.sqlQueries.length > 0 || agentState.docSearches.length > 0) && (
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? "Hide details" : "Show details"}
          </Typography>
          <Collapse in={showDetails}>
            <Box sx={{ mt: 1.5, pl: 2, borderLeft: "3px solid", borderColor: "primary.light" }}>
              {/* Render all events sorted by iteration */}
              {sortedEvents.map((event, idx) => {
                if (event.type === 'sql') {
                  const query = event.data;
                  return (
                    <Box key={`sql-${idx}`} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "success.dark", mb: 0.5 }}>
                        SQL Query (Step {query.iteration}) - {query.row_count} rows
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: "grey.900",
                          p: 1.5,
                          borderRadius: 1,
                          overflow: "auto",
                          maxHeight: 120,
                        }}
                      >
                        <Typography
                          component="pre"
                          sx={{
                            fontSize: "0.85rem",
                            fontFamily: "monospace",
                            color: "grey.100",
                            m: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          {query.sql || "SQL not available"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                } else {
                  const search = event.data;
                  return (
                    <Box key={`doc-${idx}`} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "secondary.dark", mb: 0.5 }}>
                        Doc Search (Step {search.iteration})
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: "grey.100",
                          p: 1,
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "text.primary", fontStyle: "italic" }}>
                          "{search.question}"
                        </Typography>
                      </Box>
                      {search.table_names && search.table_names.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {search.table_names.map((table, tableIdx) => (
                            <Box
                              key={tableIdx}
                              sx={{
                                backgroundColor: "secondary.light",
                                color: "secondary.dark",
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                                fontWeight: 500,
                              }}
                            >
                              {table}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                }
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
